import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Divider,
  IconButton,
  Stack,
  Switch as MUISwitch,
  useTheme,
} from "@mui/material";
import Switch from "components/basic/switch";
import { SAME_DAY_NOTICE_MINUTES } from "../../meetings/helpers/availabilitySlots";
import CloseIcon from "@mui/icons-material/Close";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddIcon from "@mui/icons-material/Add";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";

import Modal from "components/basic/modal";
import Button from "components/basic/button";
import Typography from "components/basic/typography";
import AutoComplete from "components/basic/autoComplete";

import { TimeField } from "./pickers";
import AddCustomDateModal from "./AddCustomDateModal";
import DeleteCustomDateDialog from "./DeleteCustomDateDialog";
import {
  AvailabilityData,
  BookingHorizon,
  BufferMinutes,
  CustomEntry,
  DayKey,
  WeeklyAvailability,
} from "./types";
import {
  BUFFER_OPTIONS,
  DAY_LABELS,
  DAY_ORDER,
  DEFAULT_AVAILABILITY,
  HORIZON_OPTIONS,
} from "./constants";
import { summarizeEntry } from "./helpers";

interface AvailabilityModalProps {
  initial: AvailabilityData | null;
  onClose: () => void;
  onSave: (data: AvailabilityData) => void;
}

const AvailabilityModal: React.FC<AvailabilityModalProps> = ({
  initial,
  onClose,
  onSave,
}) => {
  const theme = useTheme();
  const { t } = useTranslation("calendar");
  const base = initial ?? DEFAULT_AVAILABILITY;

  const [weekly, setWeekly] = useState<WeeklyAvailability>(base.weekly);
  const [customDates, setCustomDates] = useState<CustomEntry[]>(
    base.customDates,
  );
  const [bufferMinutes, setBufferMinutes] = useState<BufferMinutes>(
    base.bufferMinutes,
  );
  const [bookingHorizon, setBookingHorizon] = useState<BookingHorizon>(
    base.bookingHorizon,
  );
  const [allowSameDayBookings, setAllowSameDayBookings] = useState(
    base.allowSameDayBookings,
  );

  const [addOpen, setAddOpen] = useState(false);
  const [editingEntry, setEditingEntry] = useState<CustomEntry | null>(null);
  const [deletingEntry, setDeletingEntry] = useState<CustomEntry | null>(null);

  const updateDay = (day: DayKey, patch: Partial<WeeklyAvailability[DayKey]>) =>
    setWeekly((prev) => ({ ...prev, [day]: { ...prev[day], ...patch } }));

  const upsertEntry = (entry: CustomEntry) => {
    setCustomDates((prev) => {
      const exists = prev.some((e) => e.id === entry.id);
      return exists
        ? prev.map((e) => (e.id === entry.id ? entry : e))
        : [...prev, entry];
    });
    setAddOpen(false);
    setEditingEntry(null);
  };

  const handleSave = () =>
    onSave({
      weekly,
      customDates,
      bufferMinutes,
      bookingHorizon,
      allowSameDayBookings,
    });

  return (
    <Modal onClose={onClose}>
      <LocalizationProvider dateAdapter={AdapterMoment}>
        <Box
          sx={{
            width: { xs: "88vw", sm: 520 },
            maxHeight: "86vh",
            overflowY: "auto",
          }}
        >
          {/* Header */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              position: "relative",
            }}
          >
            <Typography variant="h4" style={{ fontWeight: 600 }}>
              {t("availability.title")}
            </Typography>
            <IconButton
              onClick={onClose}
              size="small"
              sx={{ position: "absolute", right: 0 }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
          <Typography
            variant="body2"
            align="center"
            style={{
              color: theme.palette.text.secondary,
              marginBottom: theme.spacing(2),
            }}
          >
            {t("availability.intro")}
          </Typography>

          {/* Column headers. Hidden once the rows stack, where they no longer
              line up with anything. */}
          <Stack
            direction="row"
            spacing={1}
            sx={{ mb: 0.5, pl: 7, display: { xs: "none", sm: "flex" } }}
          >
            <Box sx={{ width: 70 }}>
              <Typography variant="caption" style={{ fontWeight: 600 }}>
                {t("availability.available")}
              </Typography>
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant="caption" style={{ fontWeight: 600 }}>
                {t("availability.from")}
              </Typography>
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant="caption" style={{ fontWeight: 600 }}>
                {t("availability.to")}
              </Typography>
            </Box>
          </Stack>

          {/* Weekly rows */}
          <Stack spacing={1}>
            {DAY_ORDER.map((day) => {
              const value = weekly[day];
              return (
                <Box
                  key={day}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    // Day and switch keep their row; the time fields drop to a
                    // line of their own rather than being squeezed to ~50px,
                    // which clipped "09:00 AM" to "09:0".
                    flexWrap: "wrap",
                    gap: 1,
                  }}
                >
                  <Box sx={{ width: 48, flexShrink: 0 }}>
                    <Typography variant="body2" style={{ fontWeight: 600 }}>
                      {DAY_LABELS[day]}
                    </Typography>
                  </Box>
                  <Box sx={{ width: 70, flexShrink: 0 }}>
                    <MUISwitch
                      size="small"
                      color="warning"
                      checked={value.available}
                      onChange={(e) =>
                        updateDay(day, { available: e.target.checked })
                      }
                    />
                  </Box>
                  {value.available ? (
                    <Box
                      sx={{
                        display: "flex",
                        gap: 1,
                        flex: { xs: "1 1 100%", sm: "1 1 auto" },
                        minWidth: 0,
                      }}
                    >
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <TimeField
                          value={value.from}
                          minWidth={0}
                          onChange={(t) => updateDay(day, { from: t })}
                        />
                      </Box>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <TimeField
                          value={value.to}
                          minWidth={0}
                          onChange={(t) => updateDay(day, { to: t })}
                        />
                      </Box>
                    </Box>
                  ) : (
                    <Box sx={{ flex: { xs: "1 1 100%", sm: 2 } }}>
                      <Typography
                        variant="body2"
                        style={{ color: theme.palette.text.secondary }}
                      >
                        {t("availability.unavailable")}
                      </Typography>
                    </Box>
                  )}
                </Box>
              );
            })}
          </Stack>

          <Divider sx={{ my: 2 }} />

          {/* Block off particular days */}
          <Typography variant="subtitle2" style={{ fontWeight: 600 }}>
            {t("availability.block_title")}
          </Typography>
          <Typography
            variant="caption"
            style={{
              color: theme.palette.text.secondary,
              display: "block",
              marginBottom: theme.spacing(1),
            }}
          >
            {t("availability.block_intro")}
          </Typography>

          <Stack spacing={1}>
            {customDates.length === 0 ? (
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                sx={{
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: 1,
                  p: 1.25,
                }}
              >
                <CalendarMonthOutlinedIcon
                  fontSize="small"
                  sx={{ color: theme.palette.text.secondary }}
                />
                <Typography
                  variant="body2"
                  style={{ color: theme.palette.text.secondary }}
                >
                  {t("availability.no_custom")}
                </Typography>
              </Stack>
            ) : (
              customDates.map((entry) => {
                const { title, subtitle } = summarizeEntry(entry);
                return (
                  <Stack
                    key={entry.id}
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    sx={{
                      border: `1px solid ${theme.palette.divider}`,
                      borderRadius: 1,
                      p: 1.25,
                    }}
                  >
                    <CalendarMonthOutlinedIcon
                      fontSize="small"
                      sx={{ color: theme.palette.text.secondary }}
                    />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body2" style={{ fontWeight: 600 }}>
                        {title}
                      </Typography>
                      <Typography
                        variant="caption"
                        style={{ color: theme.palette.text.secondary }}
                      >
                        {subtitle}
                      </Typography>
                    </Box>
                    <IconButton
                      size="small"
                      onClick={() => setEditingEntry(entry)}
                    >
                      <EditOutlinedIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => setDeletingEntry(entry)}
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </Stack>
                );
              })
            )}

            <Box>
              <Button
                variant="outlined"
                color="warning"
                startAdornment={<AddIcon fontSize="small" />}
                onClick={() => setAddOpen(true)}
              >
                {t("availability.add_custom")}
              </Button>
            </Box>
          </Stack>

          <Divider sx={{ my: 2 }} />

          {/* Buffer + Allow bookings */}
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ mb: 1.5 }}
          >
            <Typography variant="body2" style={{ fontWeight: 600 }}>
              {t("availability.buffer")}
            </Typography>
            <Box sx={{ width: 190 }}>
              <AutoComplete
                options={BUFFER_OPTIONS.map((o) => ({
                  id: o.id,
                  label: o.label,
                }))}
                value={
                  BUFFER_OPTIONS.find((o) => o.value === bufferMinutes)?.id ?? 1
                }
                onChange={(v) => {
                  const match = BUFFER_OPTIONS.find((o) => o.id === v?.id);
                  if (match) setBufferMinutes(match.value);
                }}
              />
            </Box>
          </Stack>

          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
          >
            <Typography variant="body2" style={{ fontWeight: 600 }}>
              {t("availability.allow")}
            </Typography>
            <Box sx={{ width: 190 }}>
              <AutoComplete
                options={HORIZON_OPTIONS.map((o) => ({
                  id: o.id,
                  label: o.label,
                }))}
                value={
                  HORIZON_OPTIONS.find((o) => o.value === bookingHorizon)?.id ??
                  1
                }
                onChange={(v) => {
                  const match = HORIZON_OPTIONS.find((o) => o.id === v?.id);
                  if (match) setBookingHorizon(match.value);
                }}
              />
            </Box>
          </Stack>

          <Stack spacing={0.5}>
            <Switch
              label={t("availability.same_day")}
              checked={allowSameDayBookings}
              onChange={setAllowSameDayBookings}
            />
            <Typography
              variant="body2"
              style={{ color: theme.palette.text.secondary }}
            >
              {allowSameDayBookings
                ? t("availability.same_day_on", {
                    hours: SAME_DAY_NOTICE_MINUTES / 60,
                  })
                : t("availability.same_day_off")}
            </Typography>
          </Stack>

          <Divider sx={{ my: 2 }} />

          {/* Footer */}
          <Stack direction="row" spacing={1.5} justifyContent="flex-end">
            <Button variant="outlined" color="primary" onClick={onClose}>
              {t("availability.cancel")}
            </Button>
            <Button variant="contained" color="warning" onClick={handleSave}>
              {t("availability.save")}
            </Button>
          </Stack>
        </Box>
      </LocalizationProvider>

      {(addOpen || editingEntry) && (
        <AddCustomDateModal
          existing={editingEntry}
          onClose={() => {
            setAddOpen(false);
            setEditingEntry(null);
          }}
          onSave={upsertEntry}
        />
      )}

      {deletingEntry && (
        <DeleteCustomDateDialog
          entry={deletingEntry}
          onCancel={() => setDeletingEntry(null)}
          onConfirm={() => {
            setCustomDates((prev) =>
              prev.filter((e) => e.id !== deletingEntry.id),
            );
            setDeletingEntry(null);
          }}
        />
      )}
    </Modal>
  );
};

export default AvailabilityModal;
